import React from "react";
import CustomButton from "../../components/common/CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAdminUserProfile,
  useUserProfile,
} from "../../custom-hooks/queries";

// Skeleton loader
const ProfileSkeleton = () => (
  <div className="w-full max-w-[1200px] mx-auto px-6 py-10 animate-pulse">
    <div className="h-8 w-32 bg-gray-200 rounded"></div>
    <div className="h-4 w-64 bg-gray-200 rounded mt-2"></div>
    <div className="mt-8 border border-gray-200 rounded-2xl p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-gray-200"></div>
          <div>
            <div className="h-6 w-32 bg-gray-200 rounded"></div>
            <div className="h-4 w-48 bg-gray-200 rounded mt-2"></div>
          </div>
        </div>
        <div className="h-10 w-20 bg-gray-200 rounded"></div>
      </div>
      <hr className="my-8 border-gray-200" />
      <div className="grid grid-cols-2 gap-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-6">
            <div className="h-4 w-24 bg-gray-200 rounded"></div>
            <div className="h-5 w-40 bg-gray-200 rounded mt-2"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function AdminUserProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams();

  // Fetch user profile
  const { data, isLoading, isError } = useGetAdminUserProfile(userId);

  // Handle different response structures
  const profile = data?.data || data || {};

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (isError) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-6 py-20 text-center">
        <h2 className="text-2xl font-semibold text-gray-900">
          Failed to load profile
        </h2>
        <p className="text-gray-600 mt-2">Please try again later.</p>
      </div>
    );
  }

  const fullName =
    `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "User";

  return (
    <div className="w-full max-w-[1200px] mx-auto px-6 py-10">
      {/* TITLE */}
      <h1 className="text-3xl font-bold">Profile</h1>
      <p className="text-gray-500 mt-1">View your User profile information</p>

      {/* CARD */}
      <div className="mt-8 border border-gray-200 rounded-2xl p-8 shadow-sm">
        {/* TOP SECTION */}
        <div className="flex items-center justify-between">
          {/* LEFT: AVATAR + NAME */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-[#001F2D] flex items-center justify-center overflow-hidden">
              {profile.profileImageUrl ? (
                <img
                  src={profile.profileImageUrl}
                  className="h-full w-full object-cover"
                  alt={fullName}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <span
                className="text-white text-2xl font-semibold"
                style={{ display: profile.profileImageUrl ? "none" : "flex" }}
              >
                {profile.firstName?.[0]?.toUpperCase() || "U"}
              </span>
            </div>

            <div>
              <h2 className="text-xl font-semibold">{fullName}</h2>
              <p className="text-gray-600 text-sm">{profile.email}</p>
            </div>
          </div>
        </div>

        {/* 🔹 HORIZONTAL DIVIDER #1 (TOP) */}
        <hr className="my-8 border-gray-300" />

        {/* DETAILS GRID WITH "+ divider" */}
        <div className="relative">
          {/* 🔹 VERTICAL DIVIDER */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-gray-200" />

          {/* GRID 2×2 */}
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* LEFT COLUMN */}
            <div className="p-6 space-y-8">
              <div>
                <p className="text-gray-600 font-medium">First Name</p>
                <p className="text-gray-800 mt-1">{profile.firstName || "-"}</p>
              </div>

              <div>
                <p className="text-gray-600 font-medium">Email</p>
                <p className="text-gray-800 mt-1">{profile.email || "-"}</p>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="p-6 space-y-8">
              <div>
                <p className="text-gray-600 font-medium">Last Name</p>
                <p className="text-gray-800 mt-1">{profile.lastName || "-"}</p>
              </div>

              <div>
                <p className="text-gray-600 font-medium">Phone (optional)</p>
                <p className="text-gray-800 mt-1">{profile.phone || "-"}</p>
              </div>
            </div>
          </div>

          {/* 🔹 HORIZONTAL DIVIDER #2 (MIDDLE) → Creates the "+" shape */}
          <div className="hidden md:block absolute left-0 right-0 top-1/2 h-px bg-gray-200" />
        </div>
      </div>
    </div>
  );
}
